// Which portal this hostname serves.
//
// One Vite SPA, one Cloudflare Pages project, TWO custom domains:
//
//   portal.zooylo.app  → the caregiver portal at "/"   (admin section 301s away)
//   admin.zooylo.app   → the admin portal at "/"       (caregiver section 301s away)
//
// Anything else (localhost, *.pages.dev preview builds, a bare IP) serves BOTH
// trees the way the app always has — caregiver at "/", admin under "/admin" —
// because a preview deploy has only one hostname and cutting half the app out of
// it would make previews useless for review.
//
// The split is done in the client rather than at the edge because Cloudflare
// Pages serves one immutable asset bundle to every custom domain on the project;
// there is no per-host build. `_redirects` cannot branch on Host either. So the
// bundle ships both trees and picks at boot from window.location.hostname.
//
// Everything here is pure and takes its inputs as arguments so the routing rules
// are unit-testable without touching window or import.meta.env.

export type PortalMode = "caregiver" | "admin" | "both";

/** Production hostname serving the caregiver portal. */
export const CAREGIVER_HOST = "portal.zooylo.app";
/** Production hostname serving the admin portal. */
export const ADMIN_HOST = "admin.zooylo.app";

/**
 * Hosts that always serve both trees: local dev and Cloudflare preview deploys.
 * Matched as an exact hostname or a `.`-suffix so `foo.localhost` counts too.
 */
function isDualHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    h === "localhost" ||
    h.endsWith(".localhost") ||
    h === "127.0.0.1" ||
    h === "[::1]" ||
    h === "::1" ||
    h === "" ||
    h.endsWith(".pages.dev")
  );
}

/**
 * Resolves which portal the current hostname serves.
 *
 * `VITE_PORTAL_MODE` overrides everything — it lets a second Pages project (or a
 * local `npm run dev`) be pinned to one portal without owning the real domain.
 */
export function resolvePortalMode(
  hostname: string,
  env: { VITE_PORTAL_MODE?: string } = {},
): PortalMode {
  const override = (env.VITE_PORTAL_MODE ?? "").trim().toLowerCase();
  if (override === "admin" || override === "caregiver" || override === "both") {
    return override;
  }

  const h = (hostname ?? "").toLowerCase();

  // An explicit admin./portal. prefix wins over the dual-host default, so
  // `admin.localhost:5173` is a faithful local rehearsal of admin.zooylo.app —
  // the split is otherwise only exercisable in production, which is the worst
  // possible place to discover it is wrong. Plain `localhost` is unaffected.
  if (h === ADMIN_HOST || h.startsWith("admin.")) return "admin";
  if (h === CAREGIVER_HOST || h.startsWith("portal.")) return "caregiver";

  if (isDualHost(h)) return "both";

  // An unrecognised custom domain: serve everything rather than dead-ending on a
  // blank screen. Safe — the auth gates, not the routing, are what protect data.
  return "both";
}

/**
 * Path prefix the admin routes live under in the given mode.
 * On the admin host they ARE the root, so the prefix is empty.
 */
export function adminBase(mode: PortalMode): string {
  return mode === "admin" ? "" : "/admin";
}

/**
 * Builds an in-app admin route path. `adminPath("members", "admin")` → "/members";
 * `adminPath("members", "both")` → "/admin/members". Pass "" for the index.
 */
export function adminPath(sub: string, mode: PortalMode): string {
  const clean = sub.replace(/^\/+/, "");
  const base = adminBase(mode);
  if (clean === "") return base === "" ? "/" : base;
  return `${base}/${clean}`;
}

/**
 * Origin to send someone to when they ask for the OTHER portal on a host that
 * does not serve it. Returns null when both trees are local (dual hosts), which
 * is the caller's signal to route in-app instead of leaving the origin.
 */
export function otherPortalOrigin(
  mode: PortalMode,
  env: { VITE_ADMIN_ORIGIN?: string; VITE_CAREGIVER_ORIGIN?: string } = {},
): string | null {
  if (mode === "caregiver") {
    return (env.VITE_ADMIN_ORIGIN ?? `https://${ADMIN_HOST}`).replace(/\/+$/, "");
  }
  if (mode === "admin") {
    return (env.VITE_CAREGIVER_ORIGIN ?? `https://${CAREGIVER_HOST}`).replace(
      /\/+$/,
      "",
    );
  }
  return null;
}

// ---- Module-level resolution ------------------------------------------------
//
// The hostname cannot change without a full page load, so this is resolved once
// at import. Tests use the pure functions above, or setPortalModeForTest.

function currentHostname(): string {
  return typeof window === "undefined" ? "" : window.location.hostname;
}

let _mode: PortalMode | null = null;

/** The active {@link PortalMode} for this page load (memoised). */
export function portalMode(): PortalMode {
  if (_mode === null) {
    _mode = resolvePortalMode(
      currentHostname(),
      import.meta.env as { VITE_PORTAL_MODE?: string },
    );
  }
  return _mode;
}

/** Overrides the memoised mode — for unit tests only. Pass null to re-resolve. */
export function setPortalModeForTest(mode: PortalMode | null): void {
  _mode = mode;
}
